import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getCompanyInfo } from '@ils/config/project-config.js';
import { ExamType } from '@ils/static/enums.js';
import { getColorForExamType } from '@ils/theme/theme.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { filter, map, type Observable } from 'rxjs';
import type { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import './ils-product-card.js';

/**
 * @element ils-product-list
 */
@customElement('ils-product-list')
export class IlsProductList extends ChemistryLitElement {
  @property() private readonly examDays!: RXController<any, ExamDay[] | undefined>;
  @property({ type: String, attribute: 'exam-type' }) private readonly examType = ExamType.FAMED;

  @state() private groupedExamDays = new RXController<any, Array<[number, Array<[number, ExamDay[]]>]>>(
    this,
    undefined,
  );

  override connectedCallback(): void {
    super.connectedCallback();

    this.groupedExamDays = new RXController<
      Observable<ExamDay[] | undefined>,
      Array<[number, Array<[number, ExamDay[]]>]>
    >(this, this.examDays.observable, obs =>
      obs.pipe(
        filter(Boolean),
        map(examDays => {
          const yearGroups = new Map<number, Map<number, ExamDay[]>>();
          examDays.forEach(examDay => {
            const yearKey = examDay.start.year;
            const monthKey = examDay.start.month;
            if (!yearGroups.has(yearKey)) yearGroups.set(yearKey, new Map());
            if (!yearGroups.get(yearKey)?.has(monthKey)) yearGroups.get(yearKey)?.set(monthKey, []);
            yearGroups.get(yearKey)?.get(monthKey)?.push(examDay);
          });

          // Convert Map to array of tuples
          return Array.from(
            yearGroups.entries().map(([yearKey, monthGroups]) => [yearKey, Array.from(monthGroups.entries())]),
          );
        }),
      ),
    );
  }

  @state() private readonly user = new RXController(this, currentUser<ILSUser>());
  @state() private readonly branding = new TranslationController(this);

  override render() {
    // TODO insert filter for date range, weekdays, whatever
    // TODO remove exam days without free slots from this view?
    if (this.examDays.isLoading || this.groupedExamDays.isLoading)
      return html`
        <x-flex flex center crossaxis-center>
          <ils-loader exam-type=${this.examType || ExamType.FAMED}></ils-loader>
        </x-flex>
      `;
    if (
      !this.examDays.value ||
      this.examDays.value.length < 1 ||
      !this.groupedExamDays.value ||
      this.groupedExamDays.value.length < 1
    )
      return html` <x-text tone="subtle"> Aktuell gibt es keine Prüfungen, die gebucht werden können. </x-text>`;
    return html`
      <x-flex space="md" flex>
        ${repeat(
          this.groupedExamDays.value,
          ([year, _]) => year,
          ([year, monthGroups]) => html`
            <x-flex space="md" ${css({ position: 'relative' })}>
              <div
                ${css({
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  padding: '0.5rem 0',
                  marginTop: '-0.5rem',
                  backdropFilter: 'blur(100px)',
                })}
              >
                <x-h1 ${css({ margin: 0 })}> ${year} </x-h1>
              </div>
              ${repeat(
                monthGroups,
                ([month, _]) => month,
                ([month, examDays]) =>
                  html`
                    <x-flex space="md" ${css({ position: 'relative' })}>
                      <div
                        ${css({
                          position: 'sticky',
                          top: '2rem',
                          zIndex: 2,
                          padding: '0.5rem 0',
                          marginTop: '-0.5rem',
                          backdropFilter: 'blur(100px)',
                        })}
                      >
                        <x-h2 ${css({ margin: 0 })}>
                          ${DateTime.fromObject({ month }).setLocale('de').toFormat('MMMM')}
                        </x-h2>
                      </div>
                      ${this.renderCardGrid(examDays)}
                    </x-flex>
                  `,
              )}
            </x-flex>
          `,
        )}
      </x-flex>
    `;
  }

  private renderCardGrid(examDays: ExamDay[]): Renderable {
    //TODO how much of the location do we want to display here? currently its just the city, but displayName would also be possible. I dont think theres enough room for the full address
    return html`
      <x-grid space="md" auto-fill-columns="220px" ${css({ marginBottom: this.spacing.lg })}>
        ${repeat(
          examDays,
          d => d._id,
          examDay => {
            const [dotColor] = getColorForExamType(examDay.examType ?? ExamType.FAMED);
            const taxPercent = getCompanyInfo(examDay.examType ?? ExamType.FAMED).taxAmountPercent;
            const tax = +taxPercent ? ` (inkl. ${taxPercent}% MwSt.)` : '';
            return html`
              <ils-product-card
                flex
                .cardtitle=${html`<div
                    ${css({
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      borderRadius: '2px',
                      backgroundColor: dotColor,
                      flexShrink: 0,
                    })}
                  ></div>
                  ${examDay.location?.address?.zipCity?.city ?? ''}
                  ${examDay.start.toLocaleString({
                    ...DateTime.DATE_SHORT,
                    day: '2-digit',
                    month: '2-digit',
                  })}`}
                .subtitle=${html`
                  <x-flex space-between horizontal>
                    ${examDay.start.toLocaleString(DateTime.TIME_SIMPLE)} -
                    ${examDay.end.toLocaleString(DateTime.TIME_SIMPLE)} Uhr
                    <br />
                    ${examDay.start.weekdayLong}
                  </x-flex>
                `}
                description=${this.getDescriptionString(examDay)}
                .buttonText=${html`${+(examDay.price / 100).toFixed(2)} &euro; ${tax}`}
                ?disabled=${examDay.candidateIDs.length >= examDay.maxCandidates ||
                examDay.candidateIDs.includes(this.user.value?._id ?? '')}
                @button-click=${() =>
                  this.dispatchEvent(
                    new CustomEvent('product-selected', {
                      bubbles: true,
                      cancelable: true,
                      composed: true,
                      detail: { examDay },
                    }),
                  )}
              ></ils-product-card>
            `;
          },
        )}
      </x-grid>
    `;
  }

  private getDescriptionString(examDay: ExamDay) {
    if (examDay.candidateIDs.includes(this.user.value?._id ?? '')) return 'bereits eingetragen';
    const diff = examDay.maxCandidates - (examDay.candidateIDs.length + examDay.activeReservationsCount);
    if (diff === 0) return 'ausgebucht';
    if (diff === 1) return 'noch ein Platz frei';
    return `noch ${diff} Plätze frei`;
  }
}
